import { REHYDRATE } from 'redux-persist/es/constants';
import { MONTHLY_STORE_DISCOUNT_UPDATED } from '../../reducers/auth';
import { SORTING_OPTIONS } from '../../constants';
import {
  SET_COLLECTION_OF_CARD,
  UPDATE_COLLECTION_RECORDS_COUNT,
} from './actions';
import { compareCards, getLastCardId } from './utils';
import { CARDS_FETCH_LIMIT } from '../../config';

const INITIAL_STATE = {
  items: {},
  value: null,
  totalRecords: null,
  collectionRecords: null,
  lastFetchedCardId: null,
  invalidateOnInit: false,
  didInvalidate: true,
  fetchingInProgressSince: null,
  lastSuccessfulFetch: null,
  lastFailedFetch: null,
  lastErrorMessage: null,
  cardsSelected: [],
  sortOrder: SORTING_OPTIONS.DATE_DESCENDING,
  premiumCardExchangeInfoSeen: false,
};

export const FETCH_CARD_SUCCESSFUL = 'FETCH_CARD_SUCCESSFUL';

// eslint-disable-next-line complexity
function CardsReducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {
    case 'SET_CARDS_SELECTED':
      return {
        ...state,
        cardsSelected: action.cardsSelected,
      };
    case 'LOGOUT_SUCCESSFUL':
      return {
        ...INITIAL_STATE,
        premiumCardExchangeInfoSeen: state.premiumCardExchangeInfoSeen,
      };
    case 'SET_API_URL':
      return INITIAL_STATE;
    case 'SET_LANGUAGE':
    case 'INVALIDATE_CARDS':
      return {
        ...state,
        didInvalidate: true,
      };
    case 'FETCH_CARDS_IN_PROGRESS': {
      return {
        ...state,
        fetchingInProgressSince: action.timestamp,
      };
    }
    case 'FETCH_CARDS_SUCCESSFUL': {
      const items = { ...(action.didInvalidate ? {} : state.items) };
      const newest =
        !state.lastSuccessfulFetch || action.didInvalidate || action.all;

      action.cards.forEach((card) => {
        items[card._id] = { ...card, newest };
      });

      return {
        ...state,
        lastSuccessfulFetch: action.timestamp,
        fetchingInProgressSince: null,
        items,
        totalRecords: action.totalRecords,
        collectionRecords: action.collectionRecords,
        value: action.value,
        didInvalidate: false,
        lastFetchedCardId: getLastCardId(action.cards.map(({ _id }) => _id)),
      };
    }
    // this actions is for reseting feteched cards where there are too many fetched
    case 'RESET_CARDS': {
      const items = {};
      const newestCards = Object.keys(state.items)
        // They should be in correct order but is's done to prevent any bugs
        .sort((card1, card2) =>
          compareCards(card1, card2, SORTING_OPTIONS.DATE_ASCENDING),
        )
        .slice(0, CARDS_FETCH_LIMIT);

      newestCards.forEach((cardId) => {
        items[cardId] = state.items[cardId];
      });

      return {
        ...state,
        items,
        lastFetchedCardId: getLastCardId(newestCards),
      };
    }
    case 'FETCH_CARDS_FAILED':
      return {
        ...state,
        fetchingInProgressSince: null,
        lastFailedFetch: action.timestamp,
        lastErrorMessage: action.reason,
      };
    case 'FETCH_CARD_IN_PROGRESS': {
      return {
        ...state,
        items: {
          ...state.items,
          [action.cardId]: {
            ...(state.items[action.cardId] || {}),
            fetchingInProgressSince: action.timestamp,
          },
        },
      };
    }
    case MONTHLY_STORE_DISCOUNT_UPDATED:
    case FETCH_CARD_SUCCESSFUL: {
      const items = {
        ...state.items,
        [action.card._id]: {
          ...(action.card || {}),
          didInvalidate: false,
          fetchingInProgressSince: null,
          lastSuccessfulFetch: action.timestamp,
          language: action.language,
          newest:
            state.items[action.card._id]?.newest &&
            !action.card.cardTemplate?.hideInInventory,
        },
      };

      return {
        ...state,
        items,
      };
    }
    case 'FETCH_CARD_FAILED':
      return {
        ...state,
        items: {
          ...state.items,
          [action.cardId]: {
            ...(state.items[action.cardId] || {}),
            fetchingInProgressSince: null,
            lastFailedFetch: action.timestamp,
            lastErrorMessage: action.reason,
          },
        },
      };
    case 'ADD_CARDS': {
      if (!action.cards) {
        return state;
      }

      const cards = action.cards.filter(
        (card) => !card?.cardTemplate?.hideInInventory,
      );

      const cardsMapped = {};

      cards.reverse().forEach((card) => {
        cardsMapped[card._id] = {
          ...card,
          newest: true,
        };
      });
      // it's done like this to preserve sorting by key and make later sorting more optimized
      const items = { ...cardsMapped, ...state.items };

      const cardsValue = cards.reduce(
        (allCardsValue, card) =>
          allCardsValue + (card?.cardTemplate?.uvpValue || 0),
        0,
      );
      const value = state.value + cardsValue;
      const totalRecords = state.totalRecords + cards.length;

      return {
        ...state,
        lastSuccessfulFetch: action.timestamp,
        items,
        totalRecords,
        value,
      };
    }
    case 'GET_CARD_REWARD_SUCCESSFUL':
      return {
        ...state,
        items: {
          ...state.items,
          [action.cardId]: {
            ...state.items[action.cardId],
            reward: action.reward,
            questProgress:
              state.items[action.cardId].questProgress || action.questProgress,
            share: action.share,
          },
        },
      };
    case 'SEND_QUEST_SOLUTION_SUCCESSFUL':
      return {
        ...state,
        items: Object.assign(state.items, {
          [action.cardId]: {
            ...state.items[action.cardId],
            questProgress: action.questProgress,
          },
        }),
      };
    case 'SHRED_CARD_SUCCESSFUL':
      return {
        ...state,
        items: {
          ...state.items,
          [action.cardId]: {
            ...state.items[action.cardId],
            shredded: action.timestamp,
          },
        },
        totalRecords: state.totalRecords - 1,
        value:
          state.value -
          (state.items[action.cardId]?.cardTemplate?.uvpValue || 0),
      };
    case 'SHRED_CARDS_SUCCESSFUL': {
      const items = action.cardsIds.reduce(
        (itemsObject, cardId) => {
          const cardObj = {
            ...state.items[cardId],
            shredded: action.timestamp,
          };
          return Object.assign(itemsObject, { [cardId]: cardObj });
        },
        { ...state.items },
      );

      const shreddedStats = action.cardsIds.reduce(
        (acc, cardId) => {
          if (!state.items[cardId]?.shredded) {
            return {
              shreddedValue:
                acc.shreddedValue +
                (state.items[cardId]?.cardTemplate?.uvpValue || 0),
              shreddedCount: acc.shreddedCount + 1,
            };
          }
          return acc;
        },
        { shreddedValue: 0, shreddedCount: 0 },
      );

      const { shreddedValue, shreddedCount } = (
        Array.isArray(action.cardsNotShredded) ? action.cardsNotShredded : []
      ).reduce((acc, cardId) => {
        if (state.items[cardId]?.shredded) {
          return {
            shreddedValue:
              acc.shreddedValue -
              (state.items[cardId]?.cardTemplate?.uvpValue || 0),
            shreddedCount: acc.shreddedCount - 1,
          };
        }
        return acc;
      }, shreddedStats);

      return {
        ...state,
        items: Array.isArray(action.cardsNotShredded)
          ? action.cardsNotShredded.reduce(
              (itemsObject, cardId) => ({
                ...itemsObject,
                [cardId]: {
                  ...state.items[cardId],
                  shredded: null,
                },
              }),
              items,
            )
          : items,
        totalRecords: state.totalRecords - shreddedCount,
        value: state.value - shreddedValue,
        invalidateOnInit:
          typeof action.invalidate === 'boolean'
            ? action.invalidate
            : state.invalidateOnInit,
      };
    }
    case 'SORT_CARDS':
      if (action.sortOrder !== state.sortOrder) {
        return {
          ...state,
          sortOrder: action.sortOrder,
        };
      }
      return state;
    case 'TOGGLE_PREMIUM_CARD_EXCHANGE_INFO_SEEN':
      return {
        ...state,
        premiumCardExchangeInfoSeen: action.premiumCardExchangeInfoSeen,
      };
    case REHYDRATE:
      return {
        ...state,
        didInvalidate: state.invalidateOnInit || state.didInvalidate,
        invalidateOnInit: false,
      };
    case SET_COLLECTION_OF_CARD:
      if (!state.items?.[action.cardId]) {
        return state;
      }
      return {
        ...state,
        items: {
          ...state.items,
          [action.cardId]: {
            ...state.items[action.cardId],
            cardCollection: action.collectionId,
          },
        },
      };
    case UPDATE_COLLECTION_RECORDS_COUNT:
      if (
        typeof state.totalRecords !== 'number' ||
        typeof state.collectionRecords !== 'number' ||
        !action.collectionRecordsDiff
      ) {
        return state;
      }
      return {
        ...state,
        totalRecords: state.totalRecords - action.collectionRecordsDiff,
        collectionRecords:
          state.collectionRecords + action.collectionRecordsDiff,
      };
    default:
      return state;
  }
}

export default { cards: CardsReducer };
